
import React from "react";
import { Elements } from "./Elements";
import { i18nManager } from "../../i18n";
import { Schemes, Utils } from "../../utils";
import * as ReactRouterDom from "react-router-dom";
import { Chart, ChartData, ChartType, DOMHelper, ChartTypeRegistry, NoseurObject, Dialog, Dropdown, FormControl, NumberInput, Checkbox } from "@rivtn/noseur";

export interface ChartManagerProps {
    title: string;
    plugins?: any[];
    cannotEnlarge?: boolean;
    height?: number | string;
    style?: React.CSSProperties;
    options?: NoseurObject<any>;
    type?: string | ChartTypeRegistry;
    chartDataState: [ChartData | undefined, React.Dispatch<React.SetStateAction<ChartData | undefined>>];
}

export function ChartManager(props: ChartManagerProps) {
    const labels = i18nManager.Labels.chart;
    const [earningsChartData, _] = props.chartDataState;
    let zoomDimension = { width: "0px", height: "0px" };
    let defaultDimension = { width: "0px", height: "0px" };
    const widthInputRef = React.useRef<HTMLInputElement>(null);
    const chartElementRef = React.useRef<HTMLDivElement>(null);
    const heightInputRef = React.useRef<HTMLInputElement>(null);
    const [showMaximized, setShowMaximized] = React.useState(false);
    const chartCanvasElementRef = React.useRef<HTMLCanvasElement>(null);
    const [type, setType] = React.useState(props.type ?? ChartType.LINE);
    const [searchParams, __] = ReactRouterDom.useSearchParams(window.location.search);
    const [options, setOptions] = React.useState(props.options ?? { maintainAspectRatio: false });
    const title = `${props.title} - ${searchParams.get("from") ?? "2025-02-01 01:00:00 GMT"}_${searchParams.get("to") ?? Utils.formatToApiDate(new Date())}`;

    if (!earningsChartData) return (Elements.LOADING_STATE);

    return (<div className="chart-compound" style={props.style}>
        {(!props.cannotEnlarge) && (<i className="fa fa-maximize exp" onClick={() => setShowMaximized(true)} />)}
        <Chart className="curs-ears-chart" style={props.height ? { height: props.height } : {}} type={props.type ?? ChartType.LINE} data={earningsChartData} plugins={props.plugins} options={props.options ?? { maintainAspectRatio: false }} />
        <Dialog maximized notClosable visible={showMaximized}>
            <div className="chart-manager" ref={(r) => {
                if (r) {
                    const dimension = DOMHelper.getElementRectWithOffset(r);
                    zoomDimension.width = (dimension.width - 40) + "px";
                    zoomDimension.height = (dimension.height - 160) + "px";
                }
            }}>
                <div className="header">
                    <span className="t">{title}</span>
                    <div>
                        <span className="link danger" onClick={reset}>{labels.reset}</span>
                        <span className="link" onClick={() => Utils.downloadHtmlElement(chartCanvasElementRef.current!, `${title}.png`)}>{i18nManager.Labels.common.download}</span>
                        <span className="link" onClick={() => {
                            reset();
                            setShowMaximized(false);
                        }}>{i18nManager.Labels.common.close}</span>
                    </div>
                </div>
                <div className="control">
                    <Dropdown noIcon scheme={Schemes.OMNEHOST} options={labels.chart_types} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.type, }}
                        textInputProps={{ style: { paddingLeft: 15 } }} selectedOptionIndex={(labels.chart_types.findIndex((p) => p.value.toLowerCase() === type) ?? 0)}
                        onSelectOption={(option) => setType(option.value.toLowerCase().replaceAll("polararea", "polarArea"))} />
                    <FormControl isFieldset scheme={Schemes.OMNEHOST} label={labels.width} rightContent={<span>px</span>} style={{ marginLeft: 20, width: 130 }}>
                        <NumberInput ref={widthInputRef} onInputComplete={(v) => {
                            chartElementRef.current!.style.width = (v + "px");
                            chartCanvasElementRef.current!.style.width = (v + "px");
                        }} />
                    </FormControl>
                    <FormControl isFieldset scheme={Schemes.OMNEHOST} label={labels.height} rightContent={<span>px</span>} style={{ marginLeft: 20, width: 130 }}>
                        <NumberInput ref={heightInputRef} onInputComplete={(v) => {
                            chartElementRef.current!.style.height = (v + "px");
                            chartCanvasElementRef.current!.style.height = (v + "px");
                        }} />
                    </FormControl>
                    <Checkbox scheme={Schemes.OMNEHOST} checked={options.maintainAspectRatio} label={labels.maintain_aspect_ration} style={{ marginLeft: 20 }} onChecked={(maintainAspectRatio) => {
                        setOptions({ ...options, maintainAspectRatio });
                    }} />
                    <Checkbox scheme={Schemes.OMNEHOST} label={labels.zoom} style={{ marginLeft: 20 }} onChecked={(checked) => {
                        if (checked) {
                            changeChartSize(zoomDimension);
                            return;
                        }
                        changeChartSize(defaultDimension);
                    }} />
                </div>
                <div id="content">
                    <Chart ref={chartElementRef} canvasRef={chartCanvasElementRef} className="curs-ears-chart" style={props.height ? { height: props.height } : {}}  type={type} data={earningsChartData} options={{
                        ...options, animation: {
                            onComplete: ({ initial }: any) => {
                                if (initial && defaultDimension.width === "0px") {
                                    const elementStyle = DOMHelper.getElementStyle(chartCanvasElementRef.current);
                                    if (elementStyle.width) {
                                        defaultDimension = { width: elementStyle.width, height: elementStyle.height };
                                        if (widthInputRef.current) widthInputRef.current.value = elementStyle.width.replaceAll("px", "");
                                        if (heightInputRef.current) heightInputRef.current.value = elementStyle.height.replaceAll("px", "");
                                    }
                                }
                            }
                        }
                    }} />
                </div>
            </div>
        </Dialog>
    </div>);

    function reset() {
        setType(props.type ?? ChartType.LINE);
        setOptions(props.options ?? { maintainAspectRatio: false });

        changeChartSize(defaultDimension);
    }

    function changeChartSize(dimension: { width: string; height: string; }) {
        chartElementRef.current!.style.width = dimension.width;
        chartCanvasElementRef.current!.style.width = dimension.width;

        chartElementRef.current!.style.height = dimension.height;
        chartCanvasElementRef.current!.style.height = dimension.height;

        if (widthInputRef.current) widthInputRef.current.value = dimension.width.replaceAll("px", "");
        if (heightInputRef.current) heightInputRef.current.value = dimension.height.replaceAll("px", "");
    }

}
